import {
  type ComponentPropsWithoutRef,
  type CSSProperties,
  lazy,
  type ReactElement,
  Suspense,
} from 'react';
import type { DotLottie } from '@lottiefiles/dotlottie-react';
import { ErrorBoundary } from 'react-error-boundary';
export type { DotLottie } from '@lottiefiles/dotlottie-react';

// The dotLottie engine is ~325 KB of JS and is only needed once an animation
// actually renders, so it is loaded on demand. Keep this as `import type`
// above — a value import would defeat the split.
const DotLottieReact = lazy(() =>
  import('./internal/dot-lottie-player').then(m => ({ default: m.DotLottieReact }))
);

export interface LottiePlayerProps extends Omit<
  ComponentPropsWithoutRef<'canvas'>,
  'onError' | 'style'
> {
  autoplay?: boolean;
  loop?: boolean | number;
  speed?: number;
  src?: string;
  data?: ArrayBuffer;
  style?: CSSProperties;
  onComplete?: () => void;
  /** Fires once the animation's first frame is ready to render. */
  onDataReady?: () => void;
  onError?: (error: unknown) => void;
  dotLottieRefCallback?: (dotLottie: DotLottie) => void;
  /** Switches to another animation already bundled in a multi-animation `.lottie` manifest at `src`. */
  animationId?: string;
  /**
   * Still frame shown to users who prefer reduced motion, instead of the
   * playing animation. Defaults to the last frame, which is the resting state
   * the animation exists to arrive at. Override when the animation ends on a
   * blank frame (e.g. a fade-out).
   *
   * Pass `{ ratio }` for indeterminate animations that loop forever and so
   * have no resting state: `{ ratio: 0.5 }` picks the middle frame. The frame
   * count is only known once the animation has loaded, which is why this
   * cannot be expressed as an absolute number at the call site.
   */
  reducedMotionFrame?: number | { ratio: number };
}

/**
 * Stands in for the animation while its engine chunk is in flight.
 *
 * Renders a `div` carrying the caller's `className`, `style`, and any other
 * passthrough attributes (data-*, aria-*, role, ...), so mirroring them here
 * keeps the box at its final size (no layout shift) and keeps test hooks and
 * accessibility attributes queryable from the first paint.
 */
function EnginePlaceholder({
  className,
  style,
  passthroughProps,
}: {
  className: string | undefined;
  style: CSSProperties | undefined;
  passthroughProps: Record<string, unknown>;
}): ReactElement {
  return <div className={className} style={style} {...passthroughProps} />;
}

export function LottiePlayer({
  autoplay = true,
  className,
  loop = true,
  speed,
  src,
  data,
  style,
  onComplete,
  onDataReady,
  onError,
  dotLottieRefCallback,
  animationId,
  reducedMotionFrame,
  ...props
}: LottiePlayerProps): ReactElement {
  // ? We do not want to autoplay the lottie player if the user prefers-reduced-motion
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const canAutoplay = !prefersReducedMotion && autoplay;
  const fallback = (
    <EnginePlaceholder className={className} passthroughProps={props} style={style} />
  );

  return (
    <ErrorBoundary fallback={fallback}>
      <Suspense fallback={fallback}>
        <DotLottieReact
          {...props}
          animationId={animationId}
          autoplay={canAutoplay}
          className={className}
          data={data}
          dotLottieRefCallback={(dotLottie: DotLottie | null) => {
            if (dotLottie) {
              dotLottie.addEventListener('load', () => {
                // ? Autoplay is already off under reduced motion, but the
                // engine still paints whichever frame it settled on. Pin it
                // to a still frame so those users get a meaningful image —
                // which also makes Argos screenshots deterministic. The last
                // frame is the resting state the animation exists to arrive
                // at, matching how Argos collapses CSS animations to their
                // final keyframe. `setFrame` and `totalFrames` must be read
                // after `load`: frame data is not available before.
                if (prefersReducedMotion) {
                  const lastFrame = dotLottie.totalFrames - 1;

                  dotLottie.pause();
                  dotLottie.setFrame(
                    typeof reducedMotionFrame === 'object'
                      ? Math.round(lastFrame * reducedMotionFrame.ratio)
                      : (reducedMotionFrame ?? lastFrame)
                  );
                }
                onDataReady?.();
              });
              dotLottie.addEventListener('loadError', event => onError?.(event));
              dotLottie.addEventListener('renderError', event => onError?.(event));
              if (onComplete) {
                dotLottie.addEventListener('complete', onComplete);
              }
              dotLottieRefCallback?.(dotLottie);
            }
          }}
          loop={typeof loop === 'number' ? true : loop}
          renderConfig={{
            autoResize: true,
          }}
          speed={speed}
          src={src}
          style={style}
        />
      </Suspense>
    </ErrorBoundary>
  );
}
